/**
 * NOTE: This function formats a ISO date to a long or numeric date
 * Example long: "2024-02-14T11:05:14.785Z" => "February 14, 2024"
 * Example numeric: "2024-02-14T11:05:14.785Z" => "14.02.2024"
 * Example numericMonthYear: "2024-02-14T11:05:14.785Z" => "02.2024"
 */
export const transformDate = (
  date: string,
  structure: 'numeric' | 'long' | 'numericMonthYear' | 'longMonthYear',
  locale = 'default'
) => {
  const dateObj = new Date(date.replace(' ', 'T').replace(' UTC', 'Z'));
  const year = dateObj.getFullYear();
  const numericMonth = (dateObj.getMonth() + 1).toString().padStart(2, '0');

  if (structure === 'long') {
    const month = dateObj.toLocaleString(locale, {month: 'long'});
    return `${month} ${dateObj.getDate()}, ${year}`;
  }

  if (structure === 'longMonthYear') {
    const month = dateObj.toLocaleString(locale, {month: 'long'});
    return `${month}, ${year}`;
  }

  if (structure === 'numericMonthYear') {
    return `${numericMonth}.${year}`;
  }

  const day = dateObj.getDate().toString().padStart(2, '0');
  return `${day}.${numericMonth}.${year}`;
};

/**
 * NOTE: This function formats an ISO date to dd.mm.yyyy HH:mm (24-hour format)
 * Example: "2024-02-14T11:05:14.785Z" => "14.02.2024 11:05"
 */
export const transformDateWithTime = (date: string) => {
  const dateObj = new Date(date.replace(' ', 'T').replace(' UTC', 'Z'));
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');

  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

/**
 * NOTE: This function formats a date from YY:MM:DD to DD.MM.YYYY
 * Example: '2022-01-01' => '01.01.2022'
 */
export const transformYearMonthDayToDateMonthYear = (date: string) => {
  const dateObj = new Date(date);
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  return `${day}.${month}.${year}`;
};

/**
 * NOTE: This function formats the current date to dd/mm/yyyy
 * Example: '2022-01-01' => '01/01/2022'
 */
export const transformCurrentDateToDDMMYYYY = () => {
  const dateObj = new Date();
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const year = dateObj.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Calculates the duration between two dates and returns a formatted string.
 * Automatically selects the appropriate unit (days, months + days, or years + months).
 * @param startDate - The start date (string or Date object)
 * @param endDate - The end date (string or Date object)
 * @param locale - The locale for translations ('en' or 'de')
 * @returns Formatted duration string
 * @example
 * calculateDuration('2024-01-01', '2024-01-15', 'en'); // returns "14 days"
 * calculateDuration('2024-01-01', '2024-03-15', 'en'); // returns "2 months, 14 days"
 * calculateDuration('2020-01-01', '2024-03-15', 'en'); // returns "4 years, 2 months"
 */
export const calculateDuration = (
  startDate: string | Date,
  endDate: string | Date,
  locale: 'en' | 'de' = 'en'
): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  // Calculate total days
  const totalDays = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

  // If less than 31 days, show in days
  if (totalDays < 31) {
    const dayLabel = locale === 'de' ? (totalDays === 1 ? 'Tag' : 'Tage') : totalDays === 1 ? 'day' : 'days';
    return `${totalDays} ${dayLabel}`;
  }

  // Calculate years, months, and remaining days
  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();
  let days = end.getDate() - start.getDate();

  // Adjust if days are negative
  if (days < 0) {
    months--;
    const previousMonth = new Date(end.getFullYear(), end.getMonth(), 0);
    days += previousMonth.getDate();
  }

  // Adjust if months are negative
  if (months < 0) {
    years--;
    months += 12;
  }

  // If more than 1 year, show years + months
  if (years >= 1) {
    const yearLabel = locale === 'de' ? (years === 1 ? 'Jahr' : 'Jahre') : years === 1 ? 'year' : 'years';
    if (months === 0) {
      return `${years} ${yearLabel}`;
    }
    const monthLabel = locale === 'de' ? (months === 1 ? 'Monat' : 'Monate') : months === 1 ? 'month' : 'months';
    return `${years} ${yearLabel}, ${months} ${monthLabel}`;
  }

  // Otherwise show months + days
  const monthLabel = locale === 'de' ? (months === 1 ? 'Monat' : 'Monate') : months === 1 ? 'month' : 'months';
  if (days === 0) {
    return `${months} ${monthLabel}`;
  }
  const dayLabel = locale === 'de' ? (days === 1 ? 'Tag' : 'Tage') : days === 1 ? 'day' : 'days';
  return `${months} ${monthLabel}, ${days} ${dayLabel}`;
};
